import React, { useEffect, useState, createContext, useContext } from 'react';
import { authenticateUser, MockUser } from '../utils/mockUsers';
interface User {
  id: string;
  name: string;
  email: string;
  role: 'creator' | 'manager';
  avatar?: string;
  company?: string;
  instagramConnected?: boolean;
}
interface SignupData {
  name: string;
  email: string;
  password: string;
  role: 'creator' | 'manager';
}
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    // Check if user is already logged in
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('auth_user');
      }
    }
  }, []);
  const login = async (email: string, password: string) => {
    // Authenticate with mock data
    const authenticatedUser = authenticateUser(email, password);
    if (!authenticatedUser) {
      throw new Error('Invalid credentials');
    }
    const user: User = {
      id: authenticatedUser.id,
      name: authenticatedUser.name,
      email: authenticatedUser.email,
      role: authenticatedUser.role,
      avatar: authenticatedUser.avatar,
      company: authenticatedUser.company,
      instagramConnected: authenticatedUser.instagramConnected
    };
    setUser(user);
    localStorage.setItem('auth_user', JSON.stringify(user));
  };
  const signup = async (data: SignupData) => {
    // Create new user (in a real app, this would call an API)
    const newUser: User = {
      id: `user-${Date.now()}`,
      name: data.name,
      email: data.email,
      role: data.role,
      instagramConnected: false
    };
    setUser(newUser);
    localStorage.setItem('auth_user', JSON.stringify(newUser));
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
  };
  return <AuthContext.Provider value={{
    user,
    login,
    signup,
    logout,
    isAuthenticated: !!user
  }} data-id="element-366">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}